<template>
  <div class="min-h-screen bg-white">
    <main>
      <!-- Page Title & Overview -->
      <div class="py-16 bg-gray-50 overflow-hidden lg:py-24">
        <div class="relative max-w-xl mx-auto px-4 sm:px-6 lg:px-8 lg:max-w-7xl">
          <div class="relative">
            <h2 class="text-center text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              {{ LeadIn.Title }}
            </h2>
            <p class="mt-4 max-w-3xl mx-auto text-center text-xl text-gray-500">
              {{ LeadIn.Overview }}
            </p>
          </div>
        </div>
      </div>

      <!-- Contact Section -->
      <div class="relative bg-white">
        <div class="absolute inset-0">
          <div class="absolute inset-y-0 left-0 w-1/2" />
        </div>
        <div class="relative max-w-7xl mx-auto lg:grid lg:grid-cols-5">
          <div class="py-16 px-4 sm:px-6 lg:col-span-2 lg:px-8 lg:py-24 xl:pr-12">
            <div class="flow-root bg-gray-50 rounded-lg px-6 pb-8">
              <h2 class="mt-8 mb-8 text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">
                {{ Contact.Title }}
              </h2>
              <p class="mt-3 text-lg leading-6 text-gray-500">
                {{ Contact.Overview}}
              </p>
              <dl class="mt-8 text-base text-gray-500">
                <div class="mt-6">
                  <dt class="sr-only">Postal Address</dt>
                  <dd class="flex">
                    <OfficeBuildingIcon class="flex-shrink-0 h-6 w-6 text-gray-400" aria-hidden="true" />
                    <span class="ml-3 text-left">
                      <p>{{ Contact.Address1 }}</p>
                      <p>{{ Contact.Address2 }}</p>
                    </span>
                  </dd>
                </div>
                <div class="mt-6">
                  <dt class="sr-only">Phone number</dt>
                  <dd class="flex">
                    <PhoneIcon class="flex-shrink-0 h-6 w-6 text-gray-400" aria-hidden="true" />
                    <span class="ml-3">
                      <a :href="'tel:' + Contact.Phone">{{ Contact.Phone }}</a>
                    </span>
                  </dd>
                </div>
                <div class="mt-3">
                  <dt class="sr-only">Email</dt>
                  <dd class="flex">
                    <MailIcon class="flex-shrink-0 h-6 w-6 text-gray-400" aria-hidden="true" />
                    <span class="ml-3">
                      <a :href="'mailto:' + Contact.Email">{{ Contact.Email }}</a>
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>
          <div class="bg-white py-16 px-4 sm:px-6 lg:col-span-3 lg:py-24 lg:px-8 xl:pl-12">
            <div class="max-w-lg mx-auto lg:max-w-none">
              <form action="#" method="POST" class="grid grid-cols-1 gap-y-6">
                <div>
                  <label for="full_name" class="sr-only">Full name</label>
                  <input type="text" v-model="name" name="full_name" id="full_name" autocomplete="name" class="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md" placeholder="Full name" />
                </div>
                <div>
                  <label for="email" class="sr-only">Email</label>
                  <input id="email" v-model="email" name="email" type="email" autocomplete="email" class="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md" placeholder="Email" />
                </div>
                <div>
                  <label for="phone" class="sr-only">Phone</label>
                  <input type="text" v-model="phone" name="phone" id="phone" autocomplete="tel" class="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md" placeholder="Phone" />
                </div>
                <div>
                  <label for="message" class="sr-only">Message</label>
                  <textarea 
                    id="message" 
                    v-model="message" 
                    name="message" 
                    :rows="4" 
                    class="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md" 
                    placeholder="Message" 
                  />
                </div>
                <div>
                  <button type="button" @click="submit" class="inline-flex justify-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-awtmtn-blue hover:bg-awtmtn-dark-blue cursor-pointer" :disabled="loading">
                    <span v-if="loading">Submitting...</span>
                    <span v-else>Submit</span>
                  </button>
                  <div v-if="error" class="mt-3 text-red-500">{{ error }}</div>
                  <div v-if="success" class="mt-3 text-green-500">Message sent successfully!</div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="bg-awtmtn-navy">
        <div class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 text-center">
          <h2 class="text-3xl font-extrabold tracking-tight sm:text-4xl">
            <span class="block text-white">Looking to advance your career?</span>
            <span class="block mt-2 text-awtmtn-lite-blue">We're hiring.</span>
          </h2>
          <div class="mt-8">
            <div class="inline-flex rounded-md shadow">
              <router-link to="/careers" class="group inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-awtmtn-green hover:bg-opacity-90">
                &nbsp;&nbsp;&nbsp;&nbsp;See open positions<ExternalLinkIcon class="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
              </router-link>
            </div>
          </div>
        </div>
      </div>

    </main>
  </div>
</template>

<script>
import { LeadIn, Contact } from '../data/contact.json'
import { MailIcon, PhoneIcon, OfficeBuildingIcon } from '@heroicons/vue/outline'
import { ref } from 'vue'

export default {
  components: {
    MailIcon,
    PhoneIcon,
    OfficeBuildingIcon,
  },
  setup() {
    const name = ref('')
    const email = ref('')
    const phone = ref('')
    const message = ref('')
    const loading = ref(false)
    const error = ref('')
    const success = ref(false)

    const submit = async () => {
      if (!name.value || !email.value || !message.value) {
        error.value = 'Please fill in all required fields'
        return
      }

      loading.value = true
      error.value = ''
      success.value = false

      try {
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: name.value,
            email: email.value,
            phone: phone.value,
            message: message.value,
          }),
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.message || 'Something went wrong')
        }

        // Clear form
        name.value = ''
        email.value = ''
        phone.value = ''
        message.value = ''
        success.value = true
      } catch (err) {
        error.value = err.message
      } finally {
        loading.value = false
      }
    }

    return {
      LeadIn,
      Contact,
      name,
      email,
      phone,
      message,
      loading,
      error,
      success,
      submit,
    }
  },
}
</script>
